import type { Theme } from '@mui/material';
import { Button, Card, CardHeader, Divider, Typography, useMediaQuery } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PropertyList } from '../../../../../components/atoms/property-list';
import { PropertyListItem } from '../../../../../components/atoms/property-list-item';
import AssignReceptionsToDestinationDialog from '../../organisms/dialogs/assign-receptions-to-destination-dialog';

import { useOrganizationDevices } from '@/features/shared/hooks/query/use-organization-devices';
import { useDialog } from '@/hooks/use-dialog';

interface DestinationReceptionsProps {
  id: number;
  deviceIds: number[];
  deviceConfirmation: boolean;
}

export const DestinationReceptions: FC<DestinationReceptionsProps> = props => {
  const { id, deviceIds, deviceConfirmation, ...other } = props;
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const align = mdDown ? 'vertical' : 'horizontal';
  const { t } = useTranslation(['destination', 'common']);
  const { data: organizationDevices } = useOrganizationDevices();

  const selectedDevices = (organizationDevices || []).filter(({ id }) => deviceIds.includes(id));

  const [
    isOpenAssignReceptionsDialog,
    handleOpenAssignReceptionsDialog,
    handleCloseAssignReceptionsDialog,
  ] = useDialog();

  return (
    <>
      <Card variant="outlined" {...other}>
        <CardHeader
          title={t('destination:receptions.cardTitle')}
          action={
            <Button
              onClick={handleOpenAssignReceptionsDialog}
              color="primary"
              variant="text"
              size="large">
              {t('common:button.edit')}
            </Button>
          }
        />
        <Divider />
        <PropertyList>
          {selectedDevices.length > 0 ? (
            selectedDevices.map((device, index) => (
              <PropertyListItem
                key={device.id}
                align={align}
                label={`${index + 1}.`}
                value={device.name}
                width={200}
              />
            ))
          ) : (
            <Typography sx={{ p: 3 }} variant="inherit">
              {t('destination:receptions.noReceptions')}
            </Typography>
          )}
        </PropertyList>
      </Card>

      <AssignReceptionsToDestinationDialog
        open={isOpenAssignReceptionsDialog}
        onCancel={handleCloseAssignReceptionsDialog}
        destinationId={id}
        deviceIds={deviceIds}
        deviceConfirmation={deviceConfirmation}
      />
    </>
  );
};
